import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, UploadSimple, DownloadSimple, Trash, PencilSimple, ChatCircleDots, Package, X, FileText, Phone, ListChecks, WhatsappLogo } from "@phosphor-icons/react";
import { toast } from "sonner";
import Layout from "../components/Layout";
import api, { API } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { OwnerPill } from "../lib/usersMap";

const STATUS_COLORS = {
  new: "#3A86FF", cold: "#8D99AE", warm: "#F4A261", hot: "#D90429",
  proposal_sent: "#7C3AED", won: "#2A9D8F", lost: "#0A0A0A",
};
const STATUSES = ["new", "cold", "warm", "hot", "proposal_sent", "won", "lost"];
const SOURCES = ["IndiaMART", "TradeIndia", "Manual", "Phone", "Database", "Website", "AI Hunt", "Other"];

const empty = {
  name: "", company: "", email: "", phone: "", location: "",
  source: "Manual", status: "new", requirement: "", notes: "", value: 0,
  assigned_to: "", selected_products: [],
};

const SALES_TEAM_FALLBACK = ["inside.sales@shelvestech.com", "sales@shelvestech.com"];
const ACTIVITY_TYPES = [
  { v: "called", l: "Called" },
  { v: "no_answer", l: "No answer" },
  { v: "meeting_scheduled", l: "Meeting scheduled" },
  { v: "callback_later", l: "Callback later" },
  { v: "quote_requested", l: "Quote requested" },
  { v: "proposal_sent", l: "Proposal sent" },
  { v: "closed_won", l: "Closed won" },
  { v: "closed_lost", l: "Closed lost" },
  { v: "other", l: "Other" },
];

function formatTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}

export default function Leads() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  // Activity log dialog state
  const [activityOpen, setActivityOpen] = useState(false);
  const [activityLead, setActivityLead] = useState(null);
  const [activityList, setActivityList] = useState([]);
  const [activityDraft, setActivityDraft] = useState({ activity_type: "called", note: "" });
  const [activityBusy, setActivityBusy] = useState(false);

  const openActivity = async (lead) => {
    setActivityLead(lead);
    setActivityDraft({ activity_type: "called", note: "" });
    setActivityOpen(true);
    try {
      const { data } = await api.get(`/leads/${lead.id}/activities`);
      setActivityList(data || []);
    } catch (e) { setActivityList([]); }
  };
  const submitActivity = async () => {
    if (!activityLead) return;
    setActivityBusy(true);
    try {
      await api.post(`/leads/${activityLead.id}/activities`, activityDraft);
      toast.success("Activity logged");
      const { data } = await api.get(`/leads/${activityLead.id}/activities`);
      setActivityList(data || []);
      setActivityDraft({ activity_type: "called", note: "" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Log failed"); }
    finally { setActivityBusy(false); }
  };
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterSource, setFilterSource] = useState("all");
  const [search, setSearch] = useState("");
  const [newRemark, setNewRemark] = useState("");
  const [addingRemark, setAddingRemark] = useState(false);
  const [salesTeam, setSalesTeam] = useState(SALES_TEAM_FALLBACK);  // workspace users, email list
  const [filterAssignee, setFilterAssignee] = useState("all");
  const fileRef = useRef(null);

  const load = async (opts = {}) => {
    setLoading(true);
    try {
      const p = opts.page ?? page;
      const s = opts.search ?? search;
      const st = opts.status ?? filterStatus;
      const src = opts.source ?? filterSource;
      const { data } = await api.get("/leads", {
        params: { paginated: 1, page: p, limit, search: s, status: st, source: src },
      });
      setLeads(data.items || []);
      setTotal(data.total || 0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load({ page: 1 });
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    // Load workspace users so the assignee dropdown reflects the real team.
    api.get("/tracker/users")
      .then((r) => {
        const emails = (r.data || []).map((u) => u.email).filter(Boolean);
        // Merge unique + fallback (in case tracker/users is empty) — de-dupe & sort.
        const merged = Array.from(new Set([...emails, ...SALES_TEAM_FALLBACK])).sort();
        setSalesTeam(merged);
      })
      .catch(() => setSalesTeam(SALES_TEAM_FALLBACK));
    // eslint-disable-next-line
  }, []);

  // Debounced reload on filter/search change
  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      load({ page: 1, search, status: filterStatus, source: filterSource });
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search, filterStatus, filterSource]);

  const openNew = () => { setEditing(null); setForm(empty); setNewRemark(""); setOpen(true); };
  const openEdit = (l) => {
    setEditing(l);
    setForm({ ...empty, ...l, selected_products: l.selected_products || [] });
    setNewRemark("");
    setOpen(true);
  };

  const save = async () => {
    try {
      if (!form.name.trim()) { toast.error("Name is required"); return; }
      const payload = {
        ...form,
        value: parseFloat(form.value || 0),
        selected_products: (form.selected_products || []).map((p) => ({
          ...p,
          quantity: parseFloat(p.quantity || 0),
          unit_price: parseFloat(p.unit_price || 0),
        })),
      };
      // remarks_log is managed via dedicated endpoint; strip from payload
      delete payload.remarks_log;
      if (editing) await api.put(`/leads/${editing.id}`, payload);
      else await api.post("/leads", payload);
      toast.success(editing ? "Lead updated" : "Lead added");
      setOpen(false);
      load();
    } catch (e) {
      toast.error("Failed to save");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    try {
      await api.delete(`/leads/${id}`);
      setLeads((curr) => curr.filter((l) => l.id !== id));
      setTotal((t) => Math.max(0, t - 1));
      toast.success("Deleted");
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const sendWhatsApp = async (lead) => {
    if (!lead.phone) { toast.error("Lead has no phone number"); return; }
    if (!window.confirm(`Send WhatsApp template to ${lead.name} (${lead.phone}) via BhashSMS?`)) return;
    try {
      const { data } = await api.post("/whatsapp/send", { phone: lead.phone, lead_id: lead.id });
      toast.success(`WhatsApp sent · msg id ${data.external_message_id || "OK"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "WhatsApp send failed");
    }
  };

  const updateStatus = async (id, status) => {
    await api.put(`/leads/${id}`, { status });
    setLeads((curr) => curr.map((l) => (l.id === id ? { ...l, status } : l)));
  };

  const updateAssignee = async (id, email) => {
    const assigned_to = email === "__unassigned__" ? "" : email;
    try {
      await api.put(`/leads/${id}`, { assigned_to });
      setLeads((curr) => curr.map((l) => (l.id === id ? { ...l, assigned_to } : l)));
      toast.success(assigned_to ? `Assigned to ${assigned_to}` : "Unassigned");
    } catch (e) { toast.error("Assign failed"); }
  };

  const addRemark = async () => {
    const text = newRemark.trim();
    if (!editing || !text) return;
    setAddingRemark(true);
    try {
      const { data } = await api.post(`/leads/${editing.id}/remarks`, { text });
      setEditing(data);
      setForm((f) => ({ ...f, remarks_log: data.remarks_log }));
      setNewRemark("");
      toast.success("Remark added");
      load();
    } catch (e) {
      toast.error("Failed to add remark");
    } finally {
      setAddingRemark(false);
    }
  };

  const deleteRemark = async (remarkId) => {
    if (!editing) return;
    if (!window.confirm("Delete this remark?")) return;
    try {
      const { data } = await api.delete(`/leads/${editing.id}/remarks/${remarkId}`);
      setEditing(data);
      setForm((f) => ({ ...f, remarks_log: data.remarks_log }));
      load();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const addProduct = (productId) => {
    const p = products.find((x) => x.id === productId);
    if (!p) return;
    if ((form.selected_products || []).some((sp) => sp.product_id === p.id)) {
      toast.message("Product already added");
      return;
    }
    setForm((f) => ({
      ...f,
      selected_products: [
        ...(f.selected_products || []),
        {
          product_id: p.id,
          name: p.name,
          description: p.description || "",
          quantity: 1,
          unit_price: p.price || 0,
          unit: p.unit || "Unit",
          brochure_path: p.brochure_path || null,
          brochure_filename: p.brochure_filename || null,
        },
      ],
    }));
  };

  const updateProduct = (idx, k, v) => {
    setForm((f) => ({
      ...f,
      selected_products: f.selected_products.map((p, i) => (i === idx ? { ...p, [k]: v } : p)),
    }));
  };

  const removeProduct = (idx) => {
    setForm((f) => ({ ...f, selected_products: f.selected_products.filter((_, i) => i !== idx) }));
  };

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/leads/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const src = data.detected_source ? ` (detected: ${data.detected_source})` : "";
      const skip = data.skipped ? ` · ${data.skipped} skipped` : "";
      toast.success(`Imported ${data.inserted} leads${src}${skip}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      e.target.value = "";
    }
  };

  const exportXlsx = () => {
    window.open(`${API}/leads/export`, "_blank");
  };

  const filtered = filterAssignee === "all"
    ? leads
    : leads.filter((l) => (filterAssignee === "__unassigned__" ? !l.assigned_to : l.assigned_to === filterAssignee));

  const formatINR = (n) => `₹${(Number(n) || 0).toLocaleString("en-IN")}`;

  return (
    <Layout
      title="Leads"
      subtitle={`${leads.length} shown · ${total.toLocaleString("en-IN")} total`}
      actions={
        <>
          <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" onChange={upload} className="hidden" data-testid="upload-input" />
          <button onClick={() => fileRef.current?.click()} data-testid="upload-leads-button" className="hidden md:inline-flex items-center gap-2 px-4 py-2.5 border border-black/15 text-sm font-semibold hover:bg-[#0A0A0A] hover:text-white transition-all">
            <UploadSimple size={16} weight="bold" /> Import CSV
          </button>
          <button onClick={exportXlsx} data-testid="export-leads-button" className="hidden md:inline-flex items-center gap-2 px-4 py-2.5 border border-black/15 text-sm font-semibold hover:bg-[#0A0A0A] hover:text-white transition-all">
            <DownloadSimple size={16} weight="bold" /> Export Excel
          </button>
          <button onClick={() => fileRef.current?.click()} aria-label="Import CSV" data-testid="upload-leads-mobile" className="md:hidden p-2 border border-black/15 text-[#0A0A0A]">
            <UploadSimple size={16} weight="bold" />
          </button>
          <button onClick={openNew} data-testid="add-lead-button" className="inline-flex items-center gap-1.5 md:gap-2 px-3 md:px-4 py-2 md:py-2.5 bg-[#D90429] text-white text-xs md:text-sm font-semibold hover:bg-[#EF233C] transition-all active:scale-95">
            <Plus size={16} weight="bold" /> <span className="hidden sm:inline">Add Lead</span><span className="sm:hidden">Add</span>
          </button>
        </>
      }
    >
      {/* Filters */}
      <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-2 md:gap-3 mb-5 md:mb-6">
        <Input placeholder="Search by name, company, email…" value={search} onChange={(e) => setSearch(e.target.value)} className="w-full sm:max-w-xs rounded-none" data-testid="search-input" />
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-full sm:w-40 rounded-none" data-testid="filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterSource} onValueChange={setFilterSource}>
          <SelectTrigger className="w-full sm:w-40 rounded-none" data-testid="filter-source"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All sources</SelectItem>
            {SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterAssignee} onValueChange={setFilterAssignee}>
          <SelectTrigger className="w-full sm:w-48 rounded-none" data-testid="filter-assignee"><SelectValue placeholder="All assignees"/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All assignees</SelectItem>
            <SelectItem value="__unassigned__">— Unassigned only —</SelectItem>
            {salesTeam.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="border border-black/10 bg-white overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]" data-testid="leads-table">
          <thead className="bg-[#F5F6F8] text-left">
            <tr>
              {["Lead", "Owner", "Assigned", "Company", "Contact", "Source", "Status", "Products", "Value", ""].map((h) => (
                <th key={h} className="px-4 py-3 font-mono-shelv text-xs uppercase tracking-widest text-[#717680]">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={10} className="px-4 py-16 text-center text-[#717680]">No leads found. Try adding one or importing a CSV.</td></tr>}
            {filtered.map((l) => (
              <tr key={l.id} className="border-t border-black/5 hover:bg-[#FAFBFC]" data-testid={`lead-row-${l.id}`}>
                <td className="px-4 py-3">
                  <div className="font-semibold">{l.name}</div>
                  <div className="text-xs text-[#717680]">{l.location || "—"}</div>
                </td>
                <td className="px-4 py-3"><OwnerPill userId={l.user_id} /></td>
                <td className="px-4 py-3" data-testid={`lead-assignee-cell-${l.id}`}>
                  <Select value={l.assigned_to || "__unassigned__"} onValueChange={(v) => updateAssignee(l.id, v)}>
                    <SelectTrigger className="h-8 w-44 rounded-none text-xs" data-testid={`assign-select-${l.id}`}>
                      <SelectValue placeholder="— Unassigned —"/>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__unassigned__">— Unassigned —</SelectItem>
                      {salesTeam.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                      {l.assigned_to && !salesTeam.includes(l.assigned_to) && (
                        <SelectItem value={l.assigned_to}>{l.assigned_to} (legacy)</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-4 py-3 text-[#4A4D57]">{l.company || "—"}</td>
                <td className="px-4 py-3 text-[#4A4D57]">
                  <div>{l.email || "—"}</div>
                  <div className="text-xs text-[#717680]">{l.phone || ""}</div>
                </td>
                <td className="px-4 py-3 text-[#4A4D57]">{l.source}</td>
                <td className="px-4 py-3">
                  <Select value={l.status} onValueChange={(v) => updateStatus(l.id, v)}>
                    <SelectTrigger className="h-8 w-36 rounded-none" data-testid={`status-select-${l.id}`} style={{ background: (STATUS_COLORS[l.status] || "#0A0A0A") + "15", color: STATUS_COLORS[l.status] || "#0A0A0A", borderColor: (STATUS_COLORS[l.status] || "#0A0A0A") + "40" }}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td className="px-4 py-3 text-[#4A4D57]" data-testid={`lead-products-${l.id}`}>
                  {(l.selected_products?.length || 0) > 0 ? (
                    <span className="inline-flex items-center gap-1 text-xs"><Package size={12} weight="bold" className="text-[#D90429]" /> {l.selected_products.length}</span>
                  ) : <span className="text-[#9CA3AF]">—</span>}
                  {(l.remarks_log?.length || 0) > 0 && (
                    <span className="ml-2 inline-flex items-center gap-1 text-xs text-[#717680]"><ChatCircleDots size={12} weight="bold" /> {l.remarks_log.length}</span>
                  )}
                </td>
                <td className="px-4 py-3 font-mono-shelv text-right">{formatINR(l.value)}</td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex gap-1">
                    <button
                      onClick={() => navigate(`/proposals/new?lead=${l.id}`)}
                      className="p-1.5 hover:bg-[#D90429]/10 text-[#D90429]"
                      title="Create proposal from this lead"
                      data-testid={`make-proposal-${l.id}`}
                    >
                      <FileText size={14} weight="bold" />
                    </button>
                    <button onClick={() => openActivity(l)} className="p-1.5 hover:bg-[#F4A261]/10 text-[#F4A261]" title="Log activity" data-testid={`activity-lead-${l.id}`}><Phone size={14} /></button>
                    <button
                      onClick={() => sendWhatsApp(l)}
                      disabled={!l.phone}
                      className="p-1.5 hover:bg-[#25D366]/10 text-[#25D366] disabled:opacity-30 disabled:cursor-not-allowed"
                      title={l.phone ? `Send WhatsApp to ${l.phone} via BhashSMS` : "No phone number on this lead"}
                      data-testid={`whatsapp-lead-${l.id}`}
                    ><WhatsappLogo size={14} weight="bold" /></button>
                    <button onClick={() => openEdit(l)} className="p-1.5 hover:bg-black/5" data-testid={`edit-lead-${l.id}`}><PencilSimple size={14} /></button>
                    <button onClick={() => remove(l.id)} className="p-1.5 hover:bg-[#D90429]/10 text-[#D90429]" data-testid={`delete-lead-${l.id}`}><Trash size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination footer */}
      {total > limit && (
        <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-3 border-t border-black/10 pt-4" data-testid="leads-pagination">
          <div className="text-xs font-mono-shelv text-[#717680] tracking-widest uppercase">
            Page {page} of {Math.max(1, Math.ceil(total / limit))} · {total.toLocaleString("en-IN")} total{loading ? " · Loading…" : ""}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { const p = Math.max(1, page - 1); setPage(p); load({ page: p }); window.scrollTo({ top: 0, behavior: "smooth" }); }}
              disabled={page <= 1 || loading}
              className="px-3 py-2 border border-black/15 text-xs font-semibold hover:bg-black/5 disabled:opacity-40"
              data-testid="leads-prev-page"
            >
              ← Prev
            </button>
            <button
              onClick={() => { const p = page + 1; setPage(p); load({ page: p }); window.scrollTo({ top: 0, behavior: "smooth" }); }}
              disabled={page * limit >= total || loading}
              className="px-3 py-2 border border-black/15 text-xs font-semibold hover:bg-black/5 disabled:opacity-40"
              data-testid="leads-next-page"
            >
              Next →
            </button>
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl rounded-none max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">{editing ? "Edit lead" : "New lead"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2"><Label>Name *</Label><Input data-testid="form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none" /></div>
            <div><Label>Company</Label><Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="rounded-none" /></div>
            <div><Label>Location</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="rounded-none" /></div>
            <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-none" /></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-none" /></div>
            <div>
              <Label>Source</Label>
              <Select value={form.source} onValueChange={(v) => setForm({ ...form, source: v })}>
                <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                <SelectContent>{SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Requirement</Label><Textarea value={form.requirement} onChange={(e) => setForm({ ...form, requirement: e.target.value })} className="rounded-none" rows={2} /></div>
            <div><Label>Deal Value (₹)</Label><Input type="number" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} className="rounded-none" /></div>
            <div>
              <Label>Assigned to</Label>
              <Select value={form.assigned_to || "__unassigned__"} onValueChange={(v) => setForm({ ...form, assigned_to: v === "__unassigned__" ? "" : v })}>
                <SelectTrigger className="rounded-none" data-testid="form-assigned"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__unassigned__">— Unassigned —</SelectItem>
                  {salesTeam.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Selected Products */}
          <div className="mt-6 border-t border-black/10 pt-5">
            <div className="flex items-center justify-between mb-3">
              <div className="overline flex items-center gap-2"><Package size={14} weight="bold" /> Products of interest</div>
              <Select onValueChange={addProduct} value="">
                <SelectTrigger className="w-56 h-9 rounded-none text-sm" data-testid="add-lead-product"><SelectValue placeholder="+ Add from catalog…" /></SelectTrigger>
                <SelectContent>
                  {products.length === 0 && <SelectItem value="none" disabled>No products in catalog</SelectItem>}
                  {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} {p.price ? `· ₹${p.price}` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {(form.selected_products || []).length === 0 ? (
              <div className="text-xs text-[#9CA3AF] border border-dashed border-black/15 py-4 text-center">
                No products tagged. Add from the catalog so the proposal can auto-fill these.
              </div>
            ) : (
              <div className="space-y-2">
                {form.selected_products.map((p, idx) => (
                  <div key={p.product_id || `custom_${idx}`} className="grid grid-cols-12 gap-2 items-center" data-testid={`lead-product-row-${idx}`}>
                    <div className="col-span-5 text-sm font-medium truncate" title={p.name}>{p.name}</div>
                    <div className="col-span-2">
                      <Input type="number" value={p.quantity} onChange={(e) => updateProduct(idx, "quantity", e.target.value)} placeholder="Qty" className="rounded-none h-9 text-sm" />
                    </div>
                    <div className="col-span-2 text-xs text-[#717680]">{p.unit}</div>
                    <div className="col-span-2">
                      <Input type="number" value={p.unit_price} onChange={(e) => updateProduct(idx, "unit_price", e.target.value)} className="rounded-none h-9 text-sm" />
                    </div>
                    <button onClick={() => removeProduct(idx)} className="col-span-1 p-2 hover:bg-[#D90429]/10 text-[#D90429]" data-testid={`remove-lead-product-${idx}`}>
                      <X size={14} weight="bold" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Remarks Timeline */}
          <div className="mt-6 border-t border-black/10 pt-5">
            <div className="overline mb-3 flex items-center gap-2"><ChatCircleDots size={14} weight="bold" /> Remarks Timeline</div>
            <div className="flex gap-2 mb-3">
              <Textarea
                value={newRemark}
                onChange={(e) => setNewRemark(e.target.value)}
                placeholder={editing ? "Add a follow-up note, call summary, next action…" : "Save the lead first to add remarks."}
                rows={2}
                disabled={!editing}
                className="rounded-none flex-1"
                data-testid="new-remark-input"
              />
              <button
                onClick={addRemark}
                disabled={!editing || !newRemark.trim() || addingRemark}
                data-testid="add-remark-button"
                className="px-4 self-stretch bg-[#0A0A0A] text-white text-sm font-semibold hover:bg-[#D90429] disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                {addingRemark ? "Saving…" : "Add"}
              </button>
            </div>
            <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
              {(form.remarks_log || []).length === 0 && (
                <div className="text-xs text-[#9CA3AF] py-2">No remarks yet.</div>
              )}
              {(form.remarks_log || []).slice().reverse().map((r) => (
                <div key={r.id} className="border-l-2 border-[#D90429] bg-[#FFE5E9]/30 px-3 py-2 group" data-testid={`remark-${r.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-[11px] text-[#717680] font-mono-shelv">
                      {formatTime(r.created_at)} · {r.author_name || r.author_email || "Unknown"}
                    </div>
                    <button onClick={() => deleteRemark(r.id)} className="opacity-0 group-hover:opacity-100 transition text-[#D90429]" data-testid={`delete-remark-${r.id}`}>
                      <Trash size={12} />
                    </button>
                  </div>
                  <div className="text-sm mt-1 whitespace-pre-wrap text-[#0A0A0A]">{r.text}</div>
                </div>
              ))}
            </div>
          </div>

          <DialogFooter>
            <button onClick={() => setOpen(false)} className="px-4 py-2 border border-black/15 text-sm">Cancel</button>
            <button onClick={save} data-testid="save-lead-button" className="px-4 py-2 bg-[#D90429] text-white text-sm font-semibold hover:bg-[#EF233C]">{editing ? "Save changes" : "Add lead"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Activity log dialog */}
      <Dialog open={activityOpen} onOpenChange={setActivityOpen}>
        <DialogContent className="rounded-none max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">
              Log activity · <span className="text-[#D90429]">{activityLead?.name}</span>
              {activityLead?.assigned_to && <span className="text-xs text-[#717680] font-normal ml-2">Assigned to: {activityLead.assigned_to}</span>}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div>
              <Label>Activity type *</Label>
              <Select value={activityDraft.activity_type} onValueChange={(v) => setActivityDraft({ ...activityDraft, activity_type: v })}>
                <SelectTrigger className="rounded-none" data-testid="activity-type"><SelectValue/></SelectTrigger>
                <SelectContent>{ACTIVITY_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <button onClick={submitActivity} disabled={activityBusy} className="w-full px-3 py-2 bg-[#D90429] text-white text-sm font-semibold hover:bg-[#EF233C] disabled:opacity-50" data-testid="activity-submit">
                {activityBusy ? "Logging…" : "Log activity"}
              </button>
            </div>
            <div className="col-span-2">
              <Label>Note (optional)</Label>
              <Textarea value={activityDraft.note} onChange={(e) => setActivityDraft({ ...activityDraft, note: e.target.value })} rows={2} placeholder="e.g., Left voicemail, callback 11 AM tomorrow" className="rounded-none" data-testid="activity-note"/>
            </div>
          </div>
          <div>
            <div className="font-mono-shelv text-[10px] uppercase tracking-widest text-[#D90429] mb-2">
              <ListChecks size={12} weight="bold" className="inline mr-1"/> Recent activity ({activityList.length})
            </div>
            {activityList.length === 0 ? (
              <div className="text-sm text-[#717680] p-4 text-center border border-dashed border-black/10">No activity yet — log the first one above.</div>
            ) : (
              <div className="border border-black/10 max-h-64 overflow-y-auto" data-testid="activity-list">
                {activityList.map((a) => (
                  <div key={a.id} className="border-b border-black/5 p-3 text-[12px]">
                    <div className="flex items-center justify-between mb-1">
                      <span className="px-2 py-0.5 bg-[#F4A261]/10 text-[#F4A261] text-[10px] font-mono-shelv uppercase">{a.activity_type.replace(/_/g, " ")}</span>
                      <span className="text-[10px] text-[#717680] font-mono-shelv">{new Date(a.created_at).toLocaleString("en-IN")}</span>
                    </div>
                    {a.note && <div className="text-[#4A4D57]">{a.note}</div>}
                    {a.status_from && <div className="text-[10px] text-[#717680] font-mono-shelv mt-1">{a.status_from} → {a.status_to}</div>}
                    <div className="text-[10px] text-[#717680] mt-1">by {a.actor_email}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <button onClick={() => setActivityOpen(false)} className="px-4 py-2 border border-black/15 text-sm" data-testid="activity-close">Close</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </Layout>
  );
}
